/**
 * Sountsou 2026 - Sommaire éditorial de page (.sountsou-toc-*)
 *
 * 1. Scrollspy via IntersectionObserver pour identifier la section H2 active.
 * 2. Affichage dynamique de la capsule flottante après dépassement du sommaire de tête.
 * 3. Gestion accessible du tiroir de navigation (téléportation au body, clavier Échap, focus trap, aria-*).
 *
 * @package Sountsou2026
 */

export function initTableOfContents(root = document) {
    const cardEl = root.querySelector('.sountsou-toc-card');
    const floatingEl = root.querySelector('[data-toc-floating]');
    const drawerEl = root.querySelector('#sountsou-toc-drawer') || document.getElementById('sountsou-toc-drawer');

    if (!cardEl && !floatingEl && !drawerEl) {
        return;
    }

    // Évite la double initialisation
    if (floatingEl && floatingEl.dataset.tocInitialized === 'true') {
        return;
    }
    if (floatingEl) {
        floatingEl.dataset.tocInitialized = 'true';
    }

    // Téléporte le tiroir directement sous <body> pour s'affranchir de tout contexte
    // de confinement CSS (transform, overflow, contain) sur les conteneurs parents.
    if (drawerEl && drawerEl.parentElement !== document.body) {
        document.body.appendChild(drawerEl);
    }

    const pillBtn = floatingEl ? floatingEl.querySelector('[data-toc-pill]') : null;
    const currentLabelEl = floatingEl ? floatingEl.querySelector('[data-toc-current]') : null;
    const counterEl = floatingEl ? floatingEl.querySelector('[data-toc-counter]') : null;
    const closeBtns = drawerEl ? Array.from(drawerEl.querySelectorAll('[data-toc-drawer-close]')) : [];
    const drawerLinks = drawerEl ? Array.from(drawerEl.querySelectorAll('.sountsou-toc-drawer__link')) : [];
    const cardLinks = cardEl ? Array.from(cardEl.querySelectorAll('.sountsou-toc-card__link')) : [];

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Cartographie des cibles H2
    const targets = [];
    drawerLinks.forEach((link, index) => {
        const id = decodeURIComponent((link.hash || '').replace(/^#/, ''));
        const target = id ? document.getElementById(id) : null;
        if (target) {
            targets.push({
                index: index + 1,
                target,
                id,
                text: (link.querySelector('.sountsou-toc-drawer__label') || link).textContent.trim(),
                drawerLink: link,
                cardLink: cardLinks[index] || null,
            });
        }
    });

    const totalTargets = targets.length;

    // ------------------------------------------------------------------------
    // Mise à jour de la section active
    // ------------------------------------------------------------------------
    function setActiveSection(item) {
        if (!item) return;

        // Mise à jour du texte de la pilule
        if (currentLabelEl) {
            currentLabelEl.textContent = item.text;
        }
        if (counterEl && totalTargets > 0) {
            counterEl.textContent = `${item.index} / ${totalTargets}`;
        }

        // Mise à jour des classes et états ARIA sur les liens
        targets.forEach((t) => {
            const isActive = t === item;
            if (t.drawerLink) {
                t.drawerLink.classList.toggle('is-active', isActive);
                if (isActive) {
                    t.drawerLink.setAttribute('aria-current', 'location');
                } else {
                    t.drawerLink.removeAttribute('aria-current');
                }
            }
            if (t.cardLink) {
                t.cardLink.classList.toggle('is-active', isActive);
            }
        });
    }

    // ------------------------------------------------------------------------
    // Scrollspy : observer les H2
    // ------------------------------------------------------------------------
    if (targets.length && 'IntersectionObserver' in window) {
        const targetMap = new Map(targets.map((t) => [t.target, t]));

        const headingObserver = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

                if (visible.length > 0) {
                    const item = targetMap.get(visible[0].target);
                    if (item) {
                        setActiveSection(item);
                    }
                }
            },
            {
                rootMargin: '0px 0px -65% 0px',
                threshold: 0,
            }
        );

        targets.forEach((t) => headingObserver.observe(t.target));
    }

    // ------------------------------------------------------------------------
    // Visibilité de la capsule flottante au défilement
    // ------------------------------------------------------------------------
    if (floatingEl) {
        if (cardEl && 'IntersectionObserver' in window) {
            const cardObserver = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        // Dès que la carte sort par le haut de la fenêtre, la capsule apparaît
                        const isPastCard = !entry.isIntersecting && entry.boundingClientRect.top < 0;
                        floatingEl.classList.toggle('is-visible', isPastCard);
                        floatingEl.setAttribute('aria-hidden', String(!isPastCard));
                    });
                },
                {
                    root: null,
                    threshold: 0,
                }
            );
            cardObserver.observe(cardEl);
        } else {
            // Repli sans carte : afficher dès un scroll de 300px
            const handleScroll = () => {
                const isScrolled = window.scrollY > 300;
                floatingEl.classList.toggle('is-visible', isScrolled);
                floatingEl.setAttribute('aria-hidden', String(!isScrolled));
            };
            window.addEventListener('scroll', handleScroll, { passive: true });
            handleScroll();
        }
    }

    // ------------------------------------------------------------------------
    // Gestion du tiroir (Drawer)
    // ------------------------------------------------------------------------
    function openDrawer() {
        if (!drawerEl || !pillBtn) return;

        drawerEl.removeAttribute('hidden');
        // Force le reflow pour lancer l'animation CSS
        void drawerEl.offsetWidth;
        drawerEl.classList.add('is-open');
        pillBtn.setAttribute('aria-expanded', 'true');
        document.body.classList.add('sountsou-toc-drawer-is-open');

        // Focus sur le bouton de fermeture
        const closeBtn = drawerEl.querySelector('.sountsou-toc-drawer__close');
        if (closeBtn) {
            closeBtn.focus();
        }
    }

    function closeDrawer(restoreFocus = true) {
        if (!drawerEl || !pillBtn) return;

        drawerEl.classList.remove('is-open');
        pillBtn.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('sountsou-toc-drawer-is-open');

        const onEnd = (e) => {
            if (e.target !== drawerEl.querySelector('.sountsou-toc-drawer__panel')) return;
            drawerEl.removeEventListener('transitionend', onEnd);
            if (!drawerEl.classList.contains('is-open')) {
                drawerEl.setAttribute('hidden', '');
            }
        };

        if (prefersReducedMotion) {
            drawerEl.setAttribute('hidden', '');
        } else {
            drawerEl.addEventListener('transitionend', onEnd);
        }

        if (restoreFocus && pillBtn) {
            pillBtn.focus();
        }
    }

    if (pillBtn) {
        pillBtn.addEventListener('click', (e) => {
            e.preventDefault();
            const isOpen = pillBtn.getAttribute('aria-expanded') === 'true';
            if (isOpen) {
                closeDrawer(false);
            } else {
                openDrawer();
            }
        });
    }

    closeBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            closeDrawer(true);
        });
    });

    // Fermeture avec la touche Échap
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && drawerEl && drawerEl.classList.contains('is-open')) {
            closeDrawer(true);
        }
    });

    // Clic sur un lien dans le tiroir : fermeture automatique
    drawerLinks.forEach((link) => {
        link.addEventListener('click', () => {
            closeDrawer(false);
        });
    });
}
